import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Heart, Clock, CheckCircle, XCircle, Star } from 'lucide-react';

export default function MyInterests() {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const response = await api.get('/my-interests');
        setInterests(response.data || []);
      } catch (error) {
        console.error('Error fetching interests:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInterests();
  }, []);
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'shortlisted': return <Star className="text-yellow-500" size={16} />;
      case 'selected': return <CheckCircle className="text-green-500" size={16} />;
      case 'rejected': return <XCircle className="text-red-500" size={16} />;
      default: return <Clock className="text-neutral-400" size={16} />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'shortlisted': return 'bg-yellow-100 text-yellow-700';
      case 'selected': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };
  
  const formatBudget = (min, max) => {
    if (!min && !max) return null;
    const formatNum = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${(n/1000).toFixed(0)}K`;
    if (min && max) return `${formatNum(min)} - ${formatNum(max)}`;
    if (max) return `Up to ${formatNum(max)}`;
    return `From ${formatNum(min)}`;
  };
  
  if (loading) {
    return (
      <DashboardLayout title="My Interests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="My Interests">
      <p className="text-neutral-600 mb-6">Briefs you've expressed interest in</p>
      
      {interests.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Heart className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500 mb-2">You haven't expressed interest in any briefs yet</p>
          <Link to="/researcher/briefs" className="text-[#FF6B35] font-medium hover:underline">
            Browse Briefs
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {interests.map((interest) => (
            <div key={interest.interest_id} className="bg-white border border-neutral-200 p-6 card-researcher">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Link
                    to={`/brief/${interest.brief_id}`}
                    className="font-semibold text-lg text-neutral-900 hover:text-[#FF6B35]"
                  >
                    {interest.brief?.title}
                  </Link>
                  <p className="text-sm text-neutral-500 mt-1">
                    {interest.brief?.company_name || interest.brief?.client?.name}
                  </p>
                  
                  {interest.message && (
                    <div className="mt-3 bg-neutral-50 p-3 text-sm text-neutral-600">
                      <span className="font-medium text-neutral-700">Your message:</span>
                      <p className="mt-1">{interest.message}</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    {interest.brief?.method && (
                      <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">{interest.brief.method}</span>
                    )}
                    {interest.brief?.sector && (
                      <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">{interest.brief.sector}</span>
                    )}
                    {formatBudget(interest.brief?.budget_min, interest.brief?.budget_max) && (
                      <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">
                        {formatBudget(interest.brief?.budget_min, interest.brief?.budget_max)}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs text-neutral-400 mt-3">
                    Expressed interest {new Date(interest.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <span className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-sm ${getStatusColor(interest.status)}`}>
                  {getStatusIcon(interest.status)}
                  {interest.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
